/**
 * IndexedDB of the offline mode: the outbox of contributions waiting to be
 * sent, and the reserve of phrases fetched ahead to keep working offline.
 *
 * One small promise wrapper, no library: each call opens one transaction
 * and resolves once it has committed.
 */

const DB_NAME = 'yodi-offline';
const DB_VERSION = 1;

export const OUTBOX_STORE = 'outbox';
export const RESERVE_STORE = 'phrase-reserve';
export type OfflineStoreName = typeof OUTBOX_STORE | typeof RESERVE_STORE;

let dbPromise: Promise<IDBDatabase> | null = null;

export const isOfflineDbAvailable = () => typeof indexedDB !== 'undefined';

export function openOfflineDb(): Promise<IDBDatabase> {
  if (!isOfflineDbAvailable()) return Promise.reject(new Error('INDEXEDDB_UNAVAILABLE'));
  dbPromise ??= new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(OUTBOX_STORE)) {
        db.createObjectStore(OUTBOX_STORE, { keyPath: 'id' }).createIndex('ownerId', 'ownerId');
      }
      if (!db.objectStoreNames.contains(RESERVE_STORE)) {
        db.createObjectStore(RESERVE_STORE, { keyPath: 'key' }).createIndex('ownerId', 'ownerId');
      }
    };
    request.onsuccess = () => {
      const db = request.result;
      // Another tab opens a newer version: let it upgrade, reopen on next use.
      db.onversionchange = () => {
        db.close();
        dbPromise = null;
      };
      resolve(db);
    };
    request.onerror = () => {
      dbPromise = null;
      reject(request.error ?? new Error('INDEXEDDB_OPEN_FAILED'));
    };
  });
  return dbPromise;
}

/** Runs one request in its own transaction; resolves with its result once committed. */
export async function runRequest<T>(
  storeName: OfflineStoreName,
  mode: IDBTransactionMode,
  makeRequest: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const db = await openOfflineDb();
  return new Promise<T>((resolve, reject) => {
    const tx = db.transaction(storeName, mode);
    const request = makeRequest(tx.objectStore(storeName));
    let result: T;
    request.onsuccess = () => {
      result = request.result;
    };
    tx.oncomplete = () => resolve(result);
    tx.onerror = () => reject(tx.error ?? request.error ?? new Error('INDEXEDDB_REQUEST_FAILED'));
    tx.onabort = () => reject(tx.error ?? new Error('INDEXEDDB_ABORTED'));
  });
}

/** Tests only: forget the open connection (a fresh IDBFactory replaces the old one). */
export function resetOfflineDbForTests(): void {
  void dbPromise?.then((db) => db.close()).catch(() => undefined);
  dbPromise = null;
}
